import { byRank } from '@novachat/shared';
/**
 * Someone's standing in a channel, worked out the way the server does (apps/server/src/modules/channels/access.ts),
 * so the settings screens offer only what will be allowed (docs/plans/2026-11-03-channel-roles-design.md).
 * The server checks everything again.
 */
export function standingOf(channel, me, data) {
    const byId = new Map(data.roles.map((role) => [role.id, role]));
    const held = new Map(data.members.map((member) => [member.userId, member.roleIds]));
    // Everyone without a role of their own (in a private channel: every member) has the default role.
    const fallback = data.defaultRoleId ? byId.get(data.defaultRoleId) : undefined;
    const rolesOf = (userId) => (held.get(userId) ?? [])
        .map((id) => byId.get(id))
        .filter((role) => !!role)
        .sort((a, b) => b.position - a.position);
    /** Their highest role's position; the default role's for someone without one. */
    const rankOf = (userId) => {
        const own = rolesOf(userId);
        return own.length > 0 ? own[0].position : (fallback?.position ?? 0);
    };
    const isOwner = (userId) => rolesOf(userId).some((role) => role.template === 'owner');
    const myRank = rankOf(me.id);
    const can = (permission) => channel.permissions.includes(permission);
    const mayEdit = (role) => can('edit_roles') && role.template !== 'owner' && role.position < myRank;
    const ordered = [...data.roles].sort(byRank);
    return {
        byId,
        fallback,
        rolesOf,
        rankOf,
        isOwner,
        myRank,
        can,
        owners: data.members.filter((member) => isOwner(member.userId)).length,
        /** Roles the caller may give or take: below their own, or ownership for an owner allowed to pass it on. */
        mayHandle: (role) => (role.template === 'owner' ? isOwner(me.id) && can('grant_ownership') : role.position < myRank),
        /** Someone else the caller may change: below them, or anyone at all for an owner. */
        below: (userId) => userId !== me.id && (isOwner(me.id) || rankOf(userId) < myRank),
        /** A role the caller may give up themselves: any, except the last owner's ownership. */
        mayDrop: (role) => role.template !== 'owner' || data.members.filter((member) => isOwner(member.userId)).length > 1,
        /** A role the caller may edit: with `edit_roles`, below their own, never the owner role. */
        mayEdit,
        /** The roles in the order the list shows them, highest first. */
        ordered,
        /** Whether a role may move one step up or down: it and the role it passes are both editable by the caller. */
        mayMove: (role, direction) => {
            const at = ordered.findIndex((r) => r.id === role.id);
            const other = ordered[at + (direction === 'up' ? -1 : 1)];
            return at >= 0 && !!other && mayEdit(ordered[at]) && mayEdit(other);
        },
    };
}
//# sourceMappingURL=roles.js.map